import { AssetRow, NamirasoftBalanceServer, TransactionRow } from "namirasoft-balance";
import { IStorageLocal, TimeOperation } from "namirasoft-core";
import { NamirasoftMap } from "namirasoft-site-map";
import { DateTimeFormatter, IDFormatter, MoneyFormatter, NSBoxDateRange, NSTable, StringFormatter, StringFormatterSizeType, TableColumnInfo, TableInfo } from "namirasoft-site-react";
import { useEffect, useMemo, useRef, useState } from "react";
import Styles from "./GeneralLedger.module.css";

interface GeneralLedgerProps
{
    server: NamirasoftBalanceServer;
    namirasoft: NamirasoftMap;
    asset: AssetRow;
}

type TransactionRowWithDatetime = TransactionRow & {
    datetime: Date | null;
    journal_entry_description: string | null;
    account_name: string | null;
};

interface LedgerResponse
{
    opening_balance: number;
    closing_balance: number;
    rows: TransactionRowWithDatetime[];
    balances: number[];
    count: number;
}

interface LedgerRow extends TransactionRowWithDatetime
{
    debit: number | null;
    credit: number | null;
    balance: number;
}

function formatAmount(assetName: string, value: number): string
{
    return new MoneyFormatter(undefined, assetName).format(value);
}

export function GeneralLedger(props: GeneralLedgerProps)
{
    const assetName = props.asset.name;
    const dateRangeRef = useRef<NSBoxDateRange>(null);
    const tableRef = useRef<NSTable<LedgerRow>>(null);
    const [fromDate, setFromDate] = useState<Date>(TimeOperation.monthsAgo(1, new Date()));
    const [toDate, setToDate] = useState<Date>(new Date());
    const [data, setData] = useState<LedgerResponse | null>(null);

    const columns = useMemo<TableColumnInfo[]>(() =>
    {
        const table: TableInfo = { name: "ledger", text: "Ledger" };
        return [
            { table, index: 0, type: "String", name: "journal_entry_id", text: "Journal Entry ID", formatter: new StringFormatter(StringFormatterSizeType.FullName), defaults: { hidden: true } },
            { table, index: 1, type: "String", name: "id", text: "Transaction ID", formatter: new StringFormatter(StringFormatterSizeType.FullName), defaults: { hidden: true } },
            { table, index: 2, type: "DateTime", name: "datetime", text: "Date", formatter: new DateTimeFormatter() },
            { table, index: 3, type: "String", name: "product_id", text: "Product ID", formatter: new IDFormatter(props.namirasoft, new IStorageLocal()), defaults: { hidden: true } },
            { table, index: 4, type: "String", name: "account_name", text: "Account", formatter: new StringFormatter(StringFormatterSizeType.TwoWords) },
            { table, index: 5, type: "String", name: "txn_id", text: "Reference", formatter: new IDFormatter(props.namirasoft, new IStorageLocal()), defaults: { hidden: true } },
            { table, index: 6, type: "String", name: "journal_entry_description", text: "Journal Entry", formatter: new StringFormatter(StringFormatterSizeType.Sentence) },
            { table, index: 7, type: "String", name: "description", text: "Description", formatter: new StringFormatter(StringFormatterSizeType.Description) },
            { table, index: 8, type: "Decimal", name: "debit", text: "Debit", formatter: new MoneyFormatter("120px", assetName) },
            { table, index: 9, type: "Decimal", name: "credit", text: "Credit", formatter: new MoneyFormatter("120px", assetName) },
            { table, index: 10, type: "Decimal", name: "balance", text: "Balance", formatter: new MoneyFormatter("120px", assetName) },
        ];
    }, [assetName]);

    useEffect(() =>
    {
        tableRef.current?.reload(null, null);
        // eslint-disable-next-line
    }, [props.asset.id, fromDate?.getTime(), toDate?.getTime()]);

    const getRows = async () =>
    {
        setData(null);
        const res = await props.server.transaction.GeneralLedger(props.asset.id, fromDate.toString(), toDate.toString()) as LedgerResponse;
        setData(res);
        const txnRows: LedgerRow[] = res.rows.map((tx, i) => ({
            ...tx,
            debit: tx.amount > 0 ? tx.amount : null,
            credit: tx.amount < 0 ? Math.abs(tx.amount) : null,
            balance: res.balances[i],
        })).reverse();
        const makeBoundaryRow = (id: string, description: string, at: Date, balance: number): LedgerRow => ({
            id,
            journal_entry_id: "",
            account_id: "",
            party_id: null,
            product_id: null,
            txn_id: null,
            description,
            journal_entry_description: null,
            account_name: null,
            amount: 0,
            created_at: at,
            updated_at: at,
            datetime: at,
            debit: null,
            credit: null,
            balance,
        });
        const closingRow = makeBoundaryRow("__closing__", "--------------------------- Closing ---------------------------", toDate, res.closing_balance);
        const openingRow = makeBoundaryRow("__opening__", "--------------------------- Opening ---------------------------", fromDate, res.opening_balance);
        const rows: LedgerRow[] = [closingRow, ...txnRows, openingRow];
        return { rows, count: rows.length };
    };

    return (
        <>
            <div className={Styles.ledger_summary}>
                <div className={Styles.ledger_summary_card}>
                    <div className={Styles.ledger_summary_label}>Opening balance</div>
                    <div className={Styles.ledger_summary_value}>
                        {data ? formatAmount(assetName, data.opening_balance) : "—"}
                    </div>
                </div>
                <div className={Styles.ledger_summary_card}>
                    <div className={Styles.ledger_summary_label}>Closing balance</div>
                    <div className={Styles.ledger_summary_value}>
                        {data ? formatAmount(assetName, data.closing_balance) : "—"}
                    </div>
                </div>
            </div>
            <NSTable<LedgerRow>
                ref={tableRef}
                name="general-ledger"
                checkbox={false}
                columns={columns}
                classList={[Styles.ledger_table_grow]}
                getRows={getRows}
                getRowKey={row => row.value.id}
                sort={{ enabled: false }}
                headerChildren={
                    <NSBoxDateRange
                        ref={dateRangeRef}
                        title="Date Range"
                        required={false}
                        preset={true}
                        defaultValue={{ from: fromDate, to: toDate }}
                        onChanged={box =>
                        {
                            const value = box.getValue(false);
                            if (value)
                            {
                                setFromDate(value.from);
                                setToDate(value.to);
                            }
                        }}
                    />}
                ui={{
                    pagination: false
                }}
                getCellFormattedValue={(value, column, row, printable) =>
                {
                    const formatted = column.formatter.format(value, column, row, printable);
                    const isBoundary = row.value.id === "__opening__" || row.value.id === "__closing__";
                    if (column.name === "debit")
                        return value != null ? <span className={Styles.value_debit}>{formatted}</span> : "";
                    if (column.name === "credit")
                        return value != null ? <span className={Styles.value_credit}>{formatted}</span> : "";
                    if (column.name === "balance")
                    {
                        const cls = value > 0 ? Styles.value_debit : value < 0 ? Styles.value_credit : Styles.value_neutral;
                        return <span className={`${cls} ${isBoundary ? Styles.boundary_cell : ""}`.trim()}>{formatted}</span>;
                    }
                    if (isBoundary)
                        return column.name === "id" ? "" : <span className={Styles.boundary_cell}>{formatted}</span>;
                    return formatted;
                }}
            />
        </>
    );
}